import React, { useState, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { businessAPI } from '../../../services/businessAPI';
import SocialPostEditor from './calendar/editors/SocialPostEditor';
import VideoPostEditor from './calendar/editors/VideoPostEditor';
// Formats that use the video editor
const VIDEO_FORMATS = ['reel', 'reels', 'video', 'short', 'shorts', 'short_video', 'short_videos', 'tiktok', 'youtube_short', 'youtube_shorts'];

// Platform config
const PLATFORMS = {
  instagram: { icon: 'fab fa-instagram', color: 'bg-gradient-to-br from-pink-500 to-purple-600', label: 'IG', name: 'Instagram' },
  facebook: { icon: 'fab fa-facebook-f', color: 'bg-blue-600', label: 'FB', name: 'Facebook' },
  linkedin: { icon: 'fab fa-linkedin-in', color: 'bg-blue-700', label: 'LI', name: 'LinkedIn' },
  twitter: { icon: 'fab fa-x-twitter', color: 'bg-slate-800', label: 'X', name: 'Twitter/X' },
  tiktok: { icon: 'fab fa-tiktok', color: 'bg-slate-900', label: 'TT', name: 'TikTok' },
  youtube: { icon: 'fab fa-youtube', color: 'bg-red-600', label: 'YT', name: 'YouTube' },
  email: { icon: 'fas fa-envelope', color: 'bg-emerald-600', label: 'EM', name: 'Email' },
  googleBusiness: { icon: 'fab fa-google', color: 'bg-amber-500', label: 'GB', name: 'Google Business' }
};

const CONTENT_TYPES = {
  educational: { label: 'Edu', color: 'bg-blue-100 text-blue-700 border-blue-200' },
  entertainment: { label: 'Ent', color: 'bg-purple-100 text-purple-700 border-purple-200' },
  inspirational: { label: 'Insp', color: 'bg-amber-100 text-amber-700 border-amber-200' },
  promotional: { label: 'Promo', color: 'bg-green-100 text-green-700 border-green-200' }
};

const STATUS_STYLES = {
  draft: { bg: 'bg-white', border: 'border-slate-200', dot: 'bg-slate-300', label: 'Draft' },
  edited: { bg: 'bg-blue-50/50', border: 'border-blue-200', dot: 'bg-blue-500', label: 'Edited' },
  approved: { bg: 'bg-amber-50/50', border: 'border-amber-200', dot: 'bg-amber-500', label: 'Approved' },
  completed: { bg: 'bg-green-50/50', border: 'border-green-200', dot: 'bg-green-500', label: 'Done' }
};

const FORMAT_LABELS = {
  reel: 'Reel', reels: 'Reel', carousel: 'Carousel', story: 'Story', stories: 'Story',
  post: 'Post', video: 'Video', short: 'Short', live: 'Live', newsletter: 'Newsletter',
  standard: 'Post', blog: 'Blog', article: 'Article'
};

export default function RoadmapDashboard({ businessData, strategyData, roadmapData, onRoadmapUpdate, isFreeUser }) {
  const [calendarDays, setCalendarDays] = useState([]);
  const [selectedDay, setSelectedDay] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [viewMode, setViewMode] = useState('calendar'); // 'calendar' | 'list'
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  const FREE_DAYS_LIMIT = 14; // free/trial users can only access first 14 days

  const megaStrategy = strategyData?.megaStrategy;
  const hasMegaStrategy = megaStrategy?.calendar?.length > 0;

  const handleDayClick = (day) => {
    if (isFreeUser && day.day > FREE_DAYS_LIMIT) {
      setShowUpgradeModal(true);
      return;
    }
    setSelectedDay(day);
  };

  // Build calendar days from mega strategy
  useEffect(() => {
    const loadCalendar = async () => {
      if (!businessData || !strategyData) return;
      setIsLoading(true);

      try {
        // Try loading from database first
        const result = await businessAPI.getRoadmap();

        if (result.success && result.exists && result.roadmap?.calendar?.days?.length > 0) {
          const dbDays = result.roadmap.calendar.days;

          // Merge with localStorage edits
          const storageKey = `roadmap_${businessData?.userId || 'default'}_calendar`;
          const saved = localStorage.getItem(storageKey);

          if (saved) {
            const parsed = JSON.parse(saved);
            const merged = dbDays.map(day => {
              const local = parsed.days?.find(d => d.day === day.day);
              if (day.status === 'completed') return day;
              // Merge: DB is source of truth for content (generatedContent, caption, brief, etc.)
              // localStorage only carries lightweight status-tracking fields on top
              return local ? { ...day, ...local } : day;
            });
            setCalendarDays(merged);
          } else {
            setCalendarDays(dbDays);
          }
        } else if (hasMegaStrategy) {
          // Build from mega strategy
          const days = megaStrategy.calendar.map(entry => ({
            date: entry.date,
            day: entry.day,
            dayNumber: entry.day,
            month: 1,
            hasPost: true,
            postTitle: entry.postTitle,
            contentType: entry.contentType || 'educational',
            platform: entry.platform || 'instagram',
            pillar: entry.pillar || '',
            tone: entry.tone || 'empathetic',
            format: entry.format || 'post',
            caption: entry.caption || null,
            hashtags: entry.hashtags || [],
            priority: entry.priority || 'medium',
            status: 'draft',
            mediaUrl: null,
            generatedContent: null,
            activityType: 'social_post'
          }));

          setCalendarDays(days);

          // Save to backend
          if (onRoadmapUpdate) {
            const roadmap = {
              calendar: {
                contentPillars: megaStrategy.strategySummary?.contentPillars || [],
                contentMix: megaStrategy.strategySummary?.contentMix || {},
                days,
                totalDays: days.length,
                numMonths: 1
              },
              roadmapGenerated: true
            };
            await onRoadmapUpdate(roadmap);
          }
        }
      } catch (error) {
        console.error('Failed to load calendar:', error);
        // Fallback to mega strategy
        if (hasMegaStrategy) {
          const days = megaStrategy.calendar.map(entry => ({
            date: entry.date, day: entry.day, dayNumber: entry.day, month: 1,
            hasPost: true, postTitle: entry.postTitle,
            contentType: entry.contentType || 'educational',
            platform: entry.platform || 'instagram',
            pillar: entry.pillar || '', tone: entry.tone || 'empathetic',
            format: entry.format || 'post', caption: entry.caption || null,
            hashtags: entry.hashtags || [], priority: entry.priority || 'medium',
            status: 'draft', mediaUrl: null, generatedContent: null, activityType: 'social_post'
          }));
          setCalendarDays(days);
        }
      } finally {
        setIsLoading(false);
      }
    };

    loadCalendar();
  }, [businessData?.userId, strategyData?.userId]);

  // Save day edits - accepts (dayNumber, contentUpdates) from editors
  // Auto-saves must NOT close the panel — only explicit completion should.
  const handleDaySave = async (dayNumberOrObj, contentUpdates) => {
    let updatedDay;
    if (contentUpdates !== undefined) {
      updatedDay = { day: dayNumberOrObj, ...contentUpdates };
    } else {
      updatedDay = dayNumberOrObj;
    }
    const newDays = calendarDays.map(d => d.day === updatedDay.day ? { ...d, ...updatedDay } : d);
    setCalendarDays(newDays);

    // Only close the panel when the user explicitly marks as complete
    if (updatedDay.status === 'completed') {
      setSelectedDay(null);
    }

    // Save to localStorage — strip large content fields (generatedContent, brief, etc. live in DB)
    const storageKey = `roadmap_${businessData?.userId || 'default'}_calendar`;
    const stripDaysForLS = (days) => days.map(d => {
      const { generatedContent, brief, caption, hashtags, imageVariations,
              statusHistory, designData, suggestedHashtags, imageUrl, ...light } = d;
      // Keep server-side image paths (not data:/blob: URLs)
      if (imageUrl && !imageUrl.startsWith('data:') && !imageUrl.startsWith('blob:')) {
        light.imageUrl = imageUrl;
      }
      return light;
    });
    try {
      localStorage.setItem(storageKey, JSON.stringify({ days: stripDaysForLS(newDays) }));
    } catch (err) {
      try {
        localStorage.removeItem(storageKey);
        const ultraMinimal = newDays.map(({ day, status, hasPost, activityType, platform, postTitle, task }) =>
          ({ day, status, hasPost, activityType, platform, postTitle, task })
        );
        localStorage.setItem(storageKey, JSON.stringify({ days: ultraMinimal }));
      } catch (e2) {
        try { localStorage.removeItem(storageKey); } catch (_) {}
        console.warn('⚠️ localStorage full — using DB-only mode (data is safe in DB)');
      }
    }

    // Persist to backend — use lightweight day-level API for auto-saves
    try {
      await businessAPI.updateDayContent(updatedDay.day, contentUpdates || updatedDay);
    } catch (err) {
      console.error('Save error:', err);
    }
  };

  // Stats
  const stats = useMemo(() => {
    const total = calendarDays.length;
    const draft = calendarDays.filter(d => d.status === 'draft').length;
    const edited = calendarDays.filter(d => d.status === 'edited').length;
    const approved = calendarDays.filter(d => d.status === 'approved').length;
    const completed = calendarDays.filter(d => d.status === 'completed').length;
    const platforms = [...new Set(calendarDays.map(d => d.platform))];
    return { total, draft, edited, approved, completed, platforms };
  }, [calendarDays]);

  // Filtered days
  const filteredDays = useMemo(() => {
    if (statusFilter === 'all') return calendarDays;
    return calendarDays.filter(d => d.status === statusFilter);
  }, [calendarDays, statusFilter]);

  const isWeekdaysMode = (businessData?.brandDna?.postingFrequency || 'daily') === 'weekdays';

  // Group days by week for calendar view — date-based placement so weekday-only entries land in correct columns
  const weekGroups = useMemo(() => {
    if (!calendarDays.length) return [];

    // Get YYYY-MM-DD of the Monday of a given date
    const getMondayKey = (dateStr) => {
      const d = new Date(dateStr + 'T00:00:00');
      const dow = d.getDay(); // 0=Sun
      d.setDate(d.getDate() + (dow === 0 ? -6 : 1 - dow));
      return d.toISOString().split('T')[0];
    };

    // Place each day in its correct column (Mon=0 … Sun=6) within its week
    const weekMap = new Map();
    calendarDays.forEach(day => {
      const mondayKey = getMondayKey(day.date);
      if (!weekMap.has(mondayKey)) weekMap.set(mondayKey, Array(7).fill(null));
      const dow = new Date(day.date + 'T00:00:00').getDay();
      const col = dow === 0 ? 6 : dow - 1; // Mon=0, Tue=1, …, Sat=5, Sun=6
      weekMap.get(mondayKey)[col] = day;
    });

    return [...weekMap.keys()].sort().map(k => weekMap.get(k));
  }, [calendarDays]);

  // Progress percentage
  const progressPct = stats.total > 0 ? Math.round(((stats.edited + stats.approved + stats.completed) / stats.total) * 100) : 0;

  if (isLoading) {
    return (
      <div className="p-8">
        <div className="flex flex-col items-center justify-center py-20">
          <div className="w-12 h-12 border-3 border-blue-600 border-t-transparent rounded-full animate-spin mb-4"></div>
          <h3 className="text-sm font-semibold text-slate-700">Loading your calendar...</h3>
          <p className="text-xs text-slate-400 mt-1">Preparing your 30-day content plan</p>
        </div>
      </div>
    );
  }

  if (!calendarDays.length) {
    return (
      <div className="p-8">
        <div className="text-center py-20">
          <div className="w-16 h-16 bg-slate-100 rounded-xl flex items-center justify-center mx-auto mb-4">
            <i className="fas fa-calendar-plus text-slate-400 text-2xl"></i>
          </div>
          <h3 className="text-base font-bold text-slate-700 mb-2">No Calendar Data</h3>
          <p className="text-sm text-slate-500">Generate your strategy in Phase 2 to create your content calendar.</p>
        </div>
      </div>
    );
  }

  const today = new Date().toISOString().split('T')[0];

  return (
    <div className="p-5 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-blue-600 flex items-center justify-center">
            <i className="fas fa-calendar-alt text-white text-sm"></i>
          </div>
          <div>
            <h1 className="text-base font-bold text-slate-900">Content Calendar</h1>
            <p className="text-[11px] text-slate-500">{stats.total}-day plan for {businessData?.businessName}</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* View toggle */}
          <div className="flex items-center bg-slate-100 rounded-lg p-0.5">
            <button onClick={() => setViewMode('calendar')}
              className={`px-2.5 py-1.5 text-[11px] font-medium rounded-md transition-all ${viewMode === 'calendar' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
              <i className="fas fa-th mr-1"></i>Grid
            </button>
            <button onClick={() => setViewMode('list')}
              className={`px-2.5 py-1.5 text-[11px] font-medium rounded-md transition-all ${viewMode === 'list' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
              <i className="fas fa-list mr-1"></i>List
            </button>
          </div>
        </div>
      </div>

      {/* Progress + Stats Bar */}
      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-4">
            <div className="text-center">
              <p className="text-xl font-bold text-slate-800">{progressPct}%</p>
              <p className="text-[9px] text-slate-400 font-medium uppercase tracking-wider">Progress</p>
            </div>
            <div className="h-8 w-px bg-slate-200"></div>
            {[
              { key: 'draft', count: stats.draft, color: 'bg-slate-400', label: 'Draft' },
              { key: 'edited', count: stats.edited, color: 'bg-blue-500', label: 'Edited' },
              { key: 'approved', count: stats.approved, color: 'bg-amber-500', label: 'Approved' },
              { key: 'completed', count: stats.completed, color: 'bg-green-500', label: 'Done' }
            ].map(s => (
              <button key={s.key} onClick={() => setStatusFilter(statusFilter === s.key ? 'all' : s.key)}
                className={`flex items-center gap-1.5 px-2 py-1 rounded-md text-[11px] transition-all ${statusFilter === s.key ? 'bg-slate-100 ring-1 ring-slate-300' : 'hover:bg-slate-50'}`}>
                <span className={`w-2 h-2 rounded-full ${s.color}`}></span>
                <span className="font-semibold text-slate-700">{s.count}</span>
                <span className="text-slate-400">{s.label}</span>
              </button>
            ))}
          </div>
          {statusFilter !== 'all' && (
            <button onClick={() => setStatusFilter('all')} className="text-[10px] text-blue-600 hover:text-blue-800 font-medium">
              Show All
            </button>
          )}
        </div>

        {/* Progress bar */}
        <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden flex">
          {stats.completed > 0 && <div className="bg-green-500 transition-all" style={{ width: `${(stats.completed / stats.total) * 100}%` }}></div>}
          {stats.approved > 0 && <div className="bg-amber-400 transition-all" style={{ width: `${(stats.approved / stats.total) * 100}%` }}></div>}
          {stats.edited > 0 && <div className="bg-blue-400 transition-all" style={{ width: `${(stats.edited / stats.total) * 100}%` }}></div>}
        </div>
      </div>

      {/* Calendar Grid View */}
      {viewMode === 'calendar' && (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          {/* Day headers */}
          <div className="grid grid-cols-7 bg-slate-50 border-b border-slate-200">
            {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((d, i) => (
              <div key={d} className={`py-2 text-center text-[10px] font-bold uppercase tracking-wider ${i >= 5 && isWeekdaysMode ? 'text-slate-300 bg-slate-100' : 'text-slate-400'}`}>{d}</div>
            ))}
          </div>

          {/* Week rows */}
          {weekGroups.map((week, wi) => (
            <div key={wi} className="grid grid-cols-7 border-b border-slate-100 last:border-0">
              {week.map((day, di) => {
                if (!day) {
                  const isWeekendCol = di >= 5; // col 5=Sat, col 6=Sun
                  return (
                    <div key={di} className={`min-h-[100px] border-r border-slate-100 last:border-0 flex items-center justify-center ${isWeekendCol && isWeekdaysMode ? 'bg-slate-100/60' : 'bg-slate-50/50'}`}>
                      {isWeekendCol && isWeekdaysMode && <span className="text-[9px] text-slate-300 italic">Rest</span>}
                    </div>
                  );
                }

                const isToday = day.date === today;
                const isFiltered = statusFilter !== 'all' && day.status !== statusFilter;
                const status = STATUS_STYLES[day.status] || STATUS_STYLES.draft;
                const platform = PLATFORMS[day.platform] || PLATFORMS.instagram;
                const contentType = CONTENT_TYPES[day.contentType] || CONTENT_TYPES.educational;
                const date = new Date(day.date);
                const isWeekend = date.getDay() === 0 || date.getDay() === 6;

                const isLocked = isFreeUser && day.day > FREE_DAYS_LIMIT;

                return (
                  <div key={di}
                    onClick={() => !isFiltered && handleDayClick(day)}
                    className={`min-h-[100px] p-1.5 border-r border-slate-100 last:border-0 transition-all relative group
                      ${isFiltered ? 'opacity-20 pointer-events-none' : 'cursor-pointer'}
                      ${isLocked ? 'bg-slate-100/80' : isToday ? 'ring-2 ring-inset ring-blue-500' : ''}
                      ${isLocked ? '' : isWeekend ? 'bg-slate-50/70' : status.bg}
                      ${isLocked ? '' : 'hover:bg-blue-50/30'}
                    `}>
                    {/* Date header */}
                    <div className="flex items-center justify-between mb-1">
                      <span className={`text-[11px] font-bold ${isToday ? 'text-blue-600' : isLocked ? 'text-slate-300' : 'text-slate-500'}`}>
                        {date.getDate()}
                        {isToday && <span className="ml-1 text-[8px] font-bold text-blue-500 uppercase">Today</span>}
                      </span>
                      <div className="flex items-center gap-0.5">
                        {isLocked
                          ? <i className="fas fa-lock text-[8px] text-slate-300"></i>
                          : <span className={`w-1.5 h-1.5 rounded-full ${status.dot}`}></span>}
                      </div>
                    </div>

                    {isLocked ? (
                      /* Lock overlay for free users */
                      <div className="flex flex-col items-center justify-center py-3 text-center">
                        <i className="fas fa-lock text-slate-300 text-lg mb-1"></i>
                        <p className="text-[8px] font-bold text-slate-300 uppercase tracking-wide">Pro Only</p>
                        <p className="text-[7px] text-slate-300 mt-0.5">Day {day.day}</p>
                      </div>
                    ) : (
                      <>
                        {/* Day badge */}
                        <div className="text-[8px] font-bold text-slate-300 mb-1">D{day.day}</div>

                        {/* Platform badge */}
                        <div className={`inline-flex items-center gap-0.5 px-1 py-0.5 rounded text-[8px] font-bold text-white ${platform.color} mb-1`}>
                          <i className={`${platform.icon} text-[7px]`}></i>
                          <span>{platform.label}</span>
                        </div>

                        {/* Post title */}
                        <p className="text-[10px] font-medium text-slate-700 leading-tight line-clamp-2 mb-1">{day.postTitle}</p>

                        {/* Content type + format */}
                        <div className="flex items-center gap-1 mt-auto">
                          <span className={`text-[8px] font-semibold px-1 py-0.5 rounded border ${contentType.color}`}>
                            {contentType.label}
                          </span>
                          {day.format && (
                            <span className="text-[8px] text-slate-400 font-medium">
                              {FORMAT_LABELS[day.format] || day.format}
                            </span>
                          )}
                        </div>

                        {/* Hover overlay */}
                        <div className="absolute inset-0 bg-blue-600/5 opacity-0 group-hover:opacity-100 transition-opacity rounded pointer-events-none"></div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}

      {/* List View */}
      {viewMode === 'list' && (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          {/* Table header */}
          <div className="grid grid-cols-12 bg-slate-50 border-b border-slate-200 text-[10px] font-bold text-slate-400 uppercase tracking-wider">
            <div className="col-span-1 px-3 py-2.5">Day</div>
            <div className="col-span-2 px-3 py-2.5">Date</div>
            <div className="col-span-1 px-3 py-2.5">Platform</div>
            <div className="col-span-3 px-3 py-2.5">Post Title</div>
            <div className="col-span-1 px-3 py-2.5">Type</div>
            <div className="col-span-1 px-3 py-2.5">Format</div>
            <div className="col-span-1 px-3 py-2.5">Pillar</div>
            <div className="col-span-1 px-3 py-2.5">Status</div>
            <div className="col-span-1 px-3 py-2.5 text-right">Action</div>
          </div>

          {/* Rows */}
          {filteredDays.map((day, idx) => {
            const isToday = day.date === today;
            const status = STATUS_STYLES[day.status] || STATUS_STYLES.draft;
            const platform = PLATFORMS[day.platform] || PLATFORMS.instagram;
            const contentType = CONTENT_TYPES[day.contentType] || CONTENT_TYPES.educational;
            const date = new Date(day.date);

            const isLocked = isFreeUser && day.day > FREE_DAYS_LIMIT;
            return (
              <div key={day.day}
                onClick={() => handleDayClick(day)}
                className={`grid grid-cols-12 items-center text-xs border-b border-slate-100 last:border-0 cursor-pointer transition-all
                  ${isLocked ? 'bg-slate-50 opacity-60 hover:opacity-80' : 'hover:bg-blue-50/40'}
                  ${isToday && !isLocked ? 'bg-blue-50/60 border-l-2 border-l-blue-500' : idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/30'}
                `}>
                <div className="col-span-1 px-3 py-2.5">
                  <span className="text-[10px] font-bold text-slate-400">D{day.day}</span>
                </div>
                <div className="col-span-2 px-3 py-2.5">
                  <span className={`text-[11px] font-medium ${isToday ? 'text-blue-600 font-bold' : 'text-slate-600'}`}>
                    {date.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}
                  </span>
                  {isToday && <span className="ml-1 text-[8px] bg-blue-100 text-blue-600 px-1 rounded font-bold">TODAY</span>}
                </div>
                <div className="col-span-1 px-3 py-2.5">
                  <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold text-white ${platform.color}`}>
                    <i className={`${platform.icon} text-[8px]`}></i>
                    {platform.label}
                  </span>
                </div>
                <div className="col-span-3 px-3 py-2.5">
                  <p className="text-[11px] font-medium text-slate-700 truncate">{day.postTitle}</p>
                </div>
                <div className="col-span-1 px-3 py-2.5">
                  <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded border ${contentType.color}`}>
                    {contentType.label}
                  </span>
                </div>
                <div className="col-span-1 px-3 py-2.5">
                  <span className="text-[10px] text-slate-500">{FORMAT_LABELS[day.format] || day.format || '-'}</span>
                </div>
                <div className="col-span-1 px-3 py-2.5">
                  <span className="text-[10px] text-slate-400 truncate block">{day.pillar ? day.pillar.split(' ')[0] : '-'}</span>
                </div>
                <div className="col-span-1 px-3 py-2.5">
                  <div className="flex items-center gap-1">
                    <span className={`w-1.5 h-1.5 rounded-full ${status.dot}`}></span>
                    <span className="text-[10px] font-medium text-slate-500">{status.label}</span>
                  </div>
                </div>
                <div className="col-span-1 px-3 py-2.5 text-right">
                  <button className="text-[10px] text-blue-600 hover:text-blue-800 font-medium">
                    <i className="fas fa-pen text-[9px] mr-0.5"></i>Edit
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Platform legend */}
      <div className="bg-white border border-slate-200 rounded-xl p-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">Platforms:</span>
            {stats.platforms.map(p => {
              const plat = PLATFORMS[p];
              if (!plat) return null;
              const count = calendarDays.filter(d => d.platform === p).length;
              return (
                <div key={p} className="flex items-center gap-1">
                  <span className={`w-4 h-4 rounded flex items-center justify-center text-white text-[7px] ${plat.color}`}>
                    <i className={plat.icon}></i>
                  </span>
                  <span className="text-[10px] text-slate-600 font-medium">{plat.name}</span>
                  <span className="text-[9px] text-slate-400">({count})</span>
                </div>
              );
            })}
          </div>
          <div className="flex items-center gap-3">
            <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">Types:</span>
            {Object.entries(CONTENT_TYPES).map(([key, ct]) => {
              const count = calendarDays.filter(d => d.contentType === key).length;
              if (!count) return null;
              return (
                <div key={key} className="flex items-center gap-1">
                  <span className={`text-[8px] font-semibold px-1 py-0.5 rounded border ${ct.color}`}>{ct.label}</span>
                  <span className="text-[9px] text-slate-400">({count})</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Day Editor Panel - Portal to document.body to escape all parent CSS */}
      {selectedDay && ReactDOM.createPortal(
        <>
          <div className="fixed inset-0 bg-black bg-opacity-50 z-[9998] transition-opacity" onClick={() => setSelectedDay(null)}></div>
          <div className="fixed right-0 top-0 h-full w-full bg-white shadow-2xl z-[9999] flex flex-col"
            style={{
              animation: 'editorSlideIn 0.3s ease-out',
              maxWidth: (VIDEO_FORMATS.includes((selectedDay?.format || '').toLowerCase()) || selectedDay?.platform === 'tiktok' || selectedDay?.contentType === 'video') ? 'calc(100vw - 64px)' : '64rem',
            }}>
            {(VIDEO_FORMATS.includes((selectedDay.format || '').toLowerCase()) || selectedDay.platform === 'tiktok' || selectedDay.contentType === 'video') ? (
              <VideoPostEditor
                day={selectedDay}
                businessData={businessData}
                strategyData={strategyData}
                onSave={handleDaySave}
                onClose={() => setSelectedDay(null)}
              />
            ) : (
              <SocialPostEditor
                day={selectedDay}
                businessData={businessData}
                strategyData={strategyData}
                onSave={handleDaySave}
                onClose={() => setSelectedDay(null)}
              />
            )}
          </div>
          <style>{`
            @keyframes editorSlideIn {
              from { transform: translateX(100%); }
              to { transform: translateX(0); }
            }
          `}</style>
        </>,
        document.body
      )}
      {/* Upgrade Modal for locked days */}
      {showUpgradeModal && ReactDOM.createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
          style={{ background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }}
          onClick={() => setShowUpgradeModal(false)}>
          <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-sm w-full text-center" onClick={e => e.stopPropagation()}>
            <div className="w-16 h-16 bg-amber-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <i className="fas fa-crown text-amber-500 text-2xl"></i>
            </div>
            <h3 className="text-lg font-bold text-slate-800 mb-2">Pro Feature</h3>
            <p className="text-sm text-slate-500 mb-1">
              Days 15–30 are available on <span className="font-semibold text-slate-700">Digital Trent Pro</span>.
            </p>
            <p className="text-xs text-slate-400 mb-6">
              Your free trial gives you full access to days 1–14. Upgrade to unlock all 30 days.
            </p>
            <a href="/settings?tab=subscription"
              className="block w-full py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-semibold text-sm rounded-xl transition-colors mb-3">
              <i className="fas fa-crown mr-2"></i>Upgrade to Pro — £39/month
            </a>
            <button onClick={() => setShowUpgradeModal(false)}
              className="text-sm text-slate-400 hover:text-slate-600 transition-colors">
              Maybe later
            </button>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
